'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SelectedSnippet, SelectionRoot } from './utils';
import { getActiveSelection, getSelectedSnippet } from './utils';
import { Button } from '@/components/ui/button';
import { KeyboardKey } from '@/components/ui/keyboard-key';
import { cn, metaChar } from '@/lib/utils';
const POPUP_OFFSET_PX = 8;
const VIEWPORT_PADDING_PX = 12;
const SELECTION_SETTLE_DELAY_MS = 50;
type PopupPosition = {
  left: number;
  top: number;
  placement: 'above' | 'below';
};
type SelectionSnapshot = PopupPosition & {
  snippet: SelectedSnippet;
};

/**
 * Floating affordance for sending a text selection to chat.
 *
 * Selection ownership stays with the caller through `getSelectionRoots` and
 * `isSelectionAllowed`, because different product surfaces can render selectable
 * text in different DOM roots or have different interaction rules.
 */
type HighlightedTextPopupProps = {
  getSelectionRoots: () => readonly SelectionRoot[];
  isSelectionAllowed: (container: Element) => boolean;
  onChatClickAction: (snippet: SelectedSnippet) => void;
  className?: string;
};
function getRangeRect(range: Range): DOMRect | null {
  const boundingRect = range.getBoundingClientRect();
  if (boundingRect.width > 0 || boundingRect.height > 0) {
    return boundingRect;
  }
  const firstClientRect = range.getClientRects()[0];
  return firstClientRect ?? null;
}
function getSelectionContainer(selection: Selection): Element | null {
  const container = selection.getRangeAt(0).commonAncestorContainer;
  return container instanceof Element ? container : container.parentElement;
}
function getPopupPosition(rect: DOMRect): PopupPosition {
  const left = Math.min(Math.max(rect.left + rect.width / 2, VIEWPORT_PADDING_PX), window.innerWidth - VIEWPORT_PADDING_PX);
  const hasRoomAbove = rect.top > 44;
  return {
    left,
    top: hasRoomAbove ? rect.top - POPUP_OFFSET_PX : rect.bottom + POPUP_OFFSET_PX,
    placement: hasRoomAbove ? 'above' : 'below'
  };
}
export function HighlightedTextPopup(t0) {
  const $ = _c(27);
  const {
    getSelectionRoots,
    isSelectionAllowed,
    onChatClickAction,
    className
  } = t0;
  const [selectionSnapshot, setSelectionSnapshot] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setSelectionSnapshot(null);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const hidePopup = t1;
  let t2;
  if ($[1] !== getSelectionRoots || $[2] !== isSelectionAllowed) {
    t2 = async () => {
      const selection = getActiveSelection(getSelectionRoots());
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        hidePopup();
        return;
      }
      const container = getSelectionContainer(selection);
      if (!container || !isSelectionAllowed(container)) {
        hidePopup();
        return;
      }
      const snippet = await getSelectedSnippet(selection);
      if (!snippet) {
        hidePopup();
        return;
      }
      const rect = getRangeRect(selection.getRangeAt(0));
      if (!rect) {
        hidePopup();
        return;
      }
      setSelectionSnapshot({
        ...getPopupPosition(rect),
        snippet
      });
    };
    $[1] = getSelectionRoots;
    $[2] = isSelectionAllowed;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const updatePopup = t2;
  let t3;
  if ($[4] !== updatePopup) {
    t3 = () => {
      window.setTimeout(() => {
        updatePopup();
      }, SELECTION_SETTLE_DELAY_MS);
    };
    $[4] = updatePopup;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const schedulePopupUpdate = t3;
  let t4;
  if ($[6] !== onChatClickAction || $[7] !== selectionSnapshot) {
    t4 = () => {
      if (!selectionSnapshot) {
        hidePopup();
        return;
      }
      onChatClickAction(selectionSnapshot.snippet);
      hidePopup();
    };
    $[6] = onChatClickAction;
    $[7] = selectionSnapshot;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleChatClick = t4;
  let t5;
  let t6;
  if ($[9] !== getSelectionRoots || $[10] !== schedulePopupUpdate) {
    t5 = () => {
      const observedSelectionRoots = new Set();
      const addSelectionRootListener = root => {
        if (observedSelectionRoots.has(root)) {
          return;
        }
        root.addEventListener("selectionchange", schedulePopupUpdate);
        observedSelectionRoots.add(root);
      };
      const syncSelectionRootListeners = () => {
        getSelectionRoots().forEach(addSelectionRootListener);
      };
      const handlePointerDown = event => {
        if (event.target instanceof Element) {
          if (event.target.closest("[data-highlighted-text-popup=\"true\"]")) {
            return;
          }
        }
        hidePopup();
      };
      const handleKeyDown = event_0 => {
        if (event_0.key === "Escape") {
          hidePopup();
        }
      };
      syncSelectionRootListeners();
      const shadowRootObserver = new MutationObserver(syncSelectionRootListeners);
      shadowRootObserver.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
      document.addEventListener("mouseup", schedulePopupUpdate, true);
      document.addEventListener("pointerup", schedulePopupUpdate, true);
      document.addEventListener("keyup", schedulePopupUpdate);
      document.addEventListener("pointerdown", handlePointerDown, true);
      document.addEventListener("scroll", hidePopup, true);
      document.addEventListener("keydown", handleKeyDown, true);
      return () => {
        shadowRootObserver.disconnect();
        observedSelectionRoots.forEach(root_0 => {
          root_0.removeEventListener("selectionchange", schedulePopupUpdate);
        });
        document.removeEventListener("mouseup", schedulePopupUpdate, true);
        document.removeEventListener("pointerup", schedulePopupUpdate, true);
        document.removeEventListener("keyup", schedulePopupUpdate);
        document.removeEventListener("pointerdown", handlePointerDown, true);
        document.removeEventListener("scroll", hidePopup, true);
        document.removeEventListener("keydown", handleKeyDown, true);
      };
    };
    t6 = [getSelectionRoots, hidePopup, schedulePopupUpdate];
    $[9] = getSelectionRoots;
    $[10] = schedulePopupUpdate;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  if (!selectionSnapshot) {
    return null;
  }
  const t7 = selectionSnapshot.placement === "above" ? "-translate-x-1/2 -translate-y-full" : "-translate-x-1/2";
  let t8;
  if ($[13] !== className || $[14] !== t7) {
    t8 = cn("fixed z-100 h-8 border border-zinc-700 bg-zinc-900 px-2.5 text-zinc-100 shadow-xl hover:bg-zinc-800", t7, className);
    $[13] = className;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = `${selectionSnapshot.left}px`;
  const t10 = `${selectionSnapshot.top}px`;
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = {
      left: t9,
      top: t10
    };
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== handleChatClick) {
    t12 = event_2 => {
      event_2.preventDefault();
      event_2.stopPropagation();
      handleChatClick();
    };
    $[19] = handleChatClick;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span>Add to AI chat</span>;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="ml-1 flex items-center gap-0.5" aria-hidden="true"><KeyboardKey className="h-4 min-w-4 border-zinc-700 bg-zinc-950 px-1 text-[10px]">{metaChar()}</KeyboardKey><KeyboardKey className="h-4 min-w-4 border-zinc-700 bg-zinc-950 px-1 text-[10px]">I</KeyboardKey></span>;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t11 || $[24] !== t12 || $[25] !== t8) {
    t15 = createPortal(<Button type="button" data-highlighted-text-popup="true" aria-label="Add selection to AI chat" variant="secondary" size="sm" className={t8} style={t11} onMouseDown={_temp} onClick={t12}>{t13}{t14}</Button>, document.body);
    $[23] = t11;
    $[24] = t12;
    $[25] = t8;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  return t15;
}
function _temp(event_1) {
  event_1.preventDefault();
  event_1.stopPropagation();
}